import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { logger } from "../lib/logger";

// ------------------------------------------------------------------
// STRING DICTIONARIES
// ------------------------------------------------------------------
const STRINGS = {
    en: {
        // nav / layout
        "nav.dashboard": "Dashboard",
        "nav.help": "Help",
        "quickrules.title": "How it works",
        "quickrules.predict.t": "Predict every match",
        "quickrules.predict.d": "Pick the score for each game. Group stage and knockout points are tracked separately.",
        "quickrules.lock.t": "Locks at kick-off",
        "quickrules.lock.d": "Once a match starts, predictions freeze. Even 0–0 must be saved beforehand.",
        "quickrules.bonus.t": "Bonus points",
        "quickrules.bonus.d": "Top scorer, gold/silver/bronze, most-goals / most-conceded — auto-resolved from live results.",
        "quickrules.cta.create": "Create your own pool",
        "quickrules.cta.join": "Join with code",
        "quickrules.cta.full": "Full rules →",
        "quickrules.contact": "Contact developer",
        "nav.signin": "Sign in",
        "nav.admin_badge": "Admin",
        "layout.footer": "A friends-only World Cup prediction league",
        "lang.label": "Language",
        "lang.en": "EN",
        "lang.sv": "SV",

        // landing
        "landing.eyebrow": "Private World Cup 2026 Tipping League",
        "landing.title_1": "Pick the",
        "landing.title_emph": "exact",
        "landing.title_2": "score.",
        "landing.title_action": "Crush",
        "landing.title_3": "your friends.",
        "landing.subtitle": "Build a private pool, invite your group, and run a tipping league for every match of the 2026 World Cup. {home} per correct goal-tally + {outcome} for the right outcome (up to {max} per group game), {knock} extra for the right pen/extra-time winner on knockouts, plus {bonus} bonus points for top scorer, gold/silver/bronze and most-goals/most-conceded picks.",
        "landing.subtitle.home": "2 pts",
        "landing.subtitle.outcome": "3 pts",
        "landing.subtitle.max": "7 max",
        "landing.subtitle.knock": "+2 pts",
        "landing.subtitle.bonus": "100",
        "landing.cta.create": "Create your pool",
        "landing.cta.open": "Open dashboard",
        "landing.cta.have_account": "Login (or create account)",
        "landing.stat.teams": "{n} teams",
        "landing.stat.matches": "{n} matches in tournament",
        "landing.stat.bragging": "Created by ola.wollin@gmail.com with [[L1]]app.emergent.sh[[/L1]] · scores from [[L2]]football-data.org[[/L2]]",
        "landing.scoring.eyebrow": "The Swedish 7-point Rule",
        "landing.scoring.title_1": "One match.",
        "landing.scoring.title_2": "Three ways to score.",
        "landing.scoring.home.title": "Home goals match",
        "landing.scoring.home.desc": "You nailed the exact home score.",
        "landing.scoring.away.title": "Away goals match",
        "landing.scoring.away.desc": "You nailed the exact away score.",
        "landing.scoring.outcome.title": "Correct outcome",
        "landing.scoring.outcome.desc": "Home win, draw, or away win — right call.",
        "landing.scoring.knock.title": "Knockout pen / ET winner",
        "landing.scoring.knock.desc": "Bonus when you also pick the right side to advance after pens or extra time.",
        "landing.scoring.bonus.title": "Bonus questions",
        "landing.scoring.bonus.desc": "Top scorer (20) + gold/silver/bronze (20 each) + most-goals & most-conceded teams (10 each) = up to 100 pts.",
        "landing.scoring.max": "Max per match",
        "landing.feature.private.title": "Private pools",
        "landing.feature.private.desc": "Invite-only via a 6-char code. Your friends, your rules.",
        "landing.feature.lock.title": "Auto-locking",
        "landing.feature.lock.desc": "Predictions freeze at kickoff. No peeking, no cheating.",
        "landing.feature.leader.title": "Live leaderboard",
        "landing.feature.leader.desc": "Points recalculate the moment a result lands.",
        "landing.final.title_1": "Ready to",
        "landing.final.title_2": "tip off?",
        "landing.final.desc": "Spin up a private pool in seconds. Share the code. Let the predictions begin.",

        // auth
        "auth.login.title": "Sign in",
        "auth.login.subtitle": "Welcome back to the pool.",
        "auth.email": "Email",
        "auth.password": "Password",
        "auth.no_account": "No account?",
        "auth.create_one": "Create one",
        "auth.have_account": "Already have an account?",
        "auth.register.title": "Join the league",
        "auth.register.subtitle": "Create an account in 10 seconds.",
        "auth.name": "Display name",
        "auth.register.submit": "Create account",

        // dashboard
        "dash.eyebrow": "Your pools",
        "dash.title": "Dashboard",
        "dash.btn.join": "Join",
        "dash.btn.create": "Create pool",
        "dash.empty.title": "No pools yet",
        "dash.empty.desc": "Spin up your first private league or jump into one with an invite code from a friend.",
        "dash.members_count": "{n} members",
        "dash.modal.new": "New pool",
        "dash.modal.name": "Name",
        "dash.modal.desc": "Description (optional)",
        "dash.modal.cancel": "Cancel",
        "dash.modal.create": "Create",
        "dash.modal.creating": "Creating…",
        "dash.join.title": "Join pool",
        "dash.join.code": "Invite code",
        "dash.join.joining": "Joining…",
        "dash.join.submit": "Join",
        "dash.toast.pool_created": "Pool created · code {code}",
        "dash.toast.joined": "Joined {name}",

        // dashboard create modal — friendly bet
        "dash.modal.bet.title": "Friendly bet (optional)",
        "dash.modal.bet.hint": "Set the stake and rules. New members will see this when they join.",
        "dash.modal.bet.amount": "Amount",
        "dash.modal.bet.currency": "Currency",
        "dash.modal.bet.winner": "Who wins?",
        "dash.modal.bet.winner_ph": "e.g. Whoever tops the leaderboard at the final whistle.",
        "dash.modal.bet.instructions": "How to pay / instructions",
        "dash.modal.bet.instructions_ph": "e.g. Swish 0701234567 before the opening match.",

        // pool detail
        "pool.back_dash": "← Dashboard",
        "pool.players_one": "{n} player",
        "pool.players_other": "{n} players",
        "pool.you_admin": "You are admin",
        "pool.invite_code": "Invite code",
        "pool.copy_code": "Copy code",
        "pool.copy_link": "Copy invite link",
        "pool.tab.fixtures": "Fixtures",
        "pool.tab.groups": "Groups",
        "pool.tab.predictions": "My Predictions",
        "pool.tab.leaderboard": "Leaderboard",
        "pool.tab.admin": "Admin",
        "pool.toast.code_copied": "Invite code copied",
        "pool.toast.link_copied": "Invite link copied",
        "pool.toast.pred_saved": "Prediction saved",
        "pool.toast.recalc": "Leaderboard recalculated",

        // fixtures view
        "fix.search": "Search team or round…",
        "fix.filter.all": "all",
        "fix.filter.upcoming": "upcoming",
        "fix.filter.locked": "locked",
        "fix.empty": "No fixtures match your filter.",
        "fix.expand_all": "Expand all",
        "fix.collapse_all": "Collapse all",
        "fix.expand_next": "Next 2 matches",
        "fix.sort_label": "Sort",
        "fix.sort_kickoff": "Kick-off",
        "fix.sort_groups": "Groups",
        "fix.save_all": "Save all ({n})",
        "pool.prize_pot": "Total pot",
        "leader.group_pts": "Group",
        "leader.knock_pts": "Knockout",
        "fix.locked": "Locked",
        "fix.details": "Details →",
        "fix.save": "Save",
        "fix.saved": "Saved",
        "fix.update": "Update",
        "fix.points": "+{n} pts",
        "fix.pts_short": "pts",
        "fix.predicted_short": "Predicted",
        "fix.no_pred_short": "— No prediction —",
        "fix.save_all.prompt.title": "Save all changes?",
        "fix.save_all.prompt.body": "You have {n} unsaved predictions across multiple matches. Save them all in one go, or only the one you just clicked?",
        "fix.save_all.prompt.just_this": "Just this one",
        "fix.save_all.prompt.all": "Save all ({n})",

        // predictions view
        "preds.empty": "You haven't made any predictions yet. Switch to the Fixtures tab to start picking.",
        "preds.empty_for_pool": "No predictions in this pool yet. Predict via the Fixtures or Bracket tab in this pool.",
        "preds.pool_label": "Pool",
        "preds.copy.button": "Copy to another pool",
        "preds.copy.title": "Copy your predictions",
        "preds.copy.warning": "This will OVERWRITE every prediction you already have in the destination pool with the matching one from this pool. Matches already kicked off in the destination are skipped.",
        "preds.copy.dest_label": "Destination pool",
        "preds.copy.choose_pool": "Choose a pool…",
        "preds.copy.confirm": "Copy & overwrite",
        "preds.copy.result": "Copied: {c} · Overwritten: {o} · Locked (skipped): {s}",
        "common.cancel": "Cancel",
        "preds.label.pick": "Pick",
        "preds.label.result": "Result",
        "preds.locked": "Locked",
        "preds.open": "Open",

        // leaderboard
        "leader.empty.title": "No scores yet. They'll appear once results land.",
        "leader.empty.force": "Force recalculation",
        "leader.title": "Standings",
        "leader.refresh": "Refresh",
        "leader.recalc": "Recalculate",
        "leader.summary.title": "League Summary Graphic",
        "leader.summary.subtitle": "Download a shareable PNG with the podium, prize pot & every player's points",
        "leader.summary.cta": "Open",
        "leader.pts": "pts",
        "leader.csv": "Export CSV",
        "leader.mvp": "MVP",
        "leader.mvp_round": "MVP — {round}",
        "leader.climber": "Biggest climber",
        "leader.dropper": "Biggest fall",
        "leader.you": "YOU",

        // admin
        "admin.scheduler.fixtures.title": "Fixtures sync · every {n} min",
        "admin.scheduler.on": "On",
        "admin.scheduler.off": "Off",
        "admin.scheduler.next": "Next run:",
        "admin.scheduler.last": "Last:",
        "admin.scheduler.summary": "{updated} updated · {changed} new",
        "admin.scheduler.refresh": "Refresh",
        "admin.scheduler.run_now": "Run now",
        "admin.scheduler.syncing": "Syncing…",
        "admin.live.title": "Live poll · every {n} min",
        "admin.live.match_live": "Match Live",
        "admin.live.idle": "Idle",
        "admin.live.polling_desc": "Polling /fixtures?live=all for current match goals.",
        "admin.live.skip_desc": "Skipping API calls — no match in its kickoff window.",
        "admin.live.next_check": "Next check:",
        "admin.live.last": "Last:",
        "admin.live.summary": "{updated} updated · {changed} changes",
        "admin.live.poll_now": "Poll now",
        "admin.live.polling": "Polling…",
        "admin.live.toast.skipped": "No active match window — live poll skipped",
        "admin.live.toast.ok": "Live poll · {updated} matches checked · {changed} changes",
        "admin.sync.teams.title": "Sync teams",
        "admin.sync.teams.desc": "Fetch teams from API-Football",
        "admin.sync.fixtures.title": "Sync fixtures + results",
        "admin.sync.fixtures.desc": "Import fixtures & latest results",
        "admin.recalc.title": "Recalculate scores",
        "admin.recalc.desc": "Rebuild this pool's leaderboard",
        "admin.edit.title": "Edit results manually",
        "admin.search": "Search team or round…",
        "admin.edit_btn": "Edit",
        "admin.modal.set_result": "Set result",
        "admin.modal.cancel": "Cancel",
        "admin.modal.save": "Save",
        "admin.modal.penalty_title": "Penalty-shootout winner",
        "admin.modal.penalty_hint": "Match ended level. Pick the team that won on penalties so the bracket advances correctly.",
        "admin.toast.sync_ok": "{kind} synced",
        "admin.toast.result_saved": "Result saved + leaderboard recalculated",
        "admin.toast.synced_full": "Synced · {updated} updated · {changed} results changed",

        // match detail
        "match.back": "← Back",
        "match.number.label": "Match #",
        "summary.title.eyebrow": "Season recap",
        "summary.title": "Tournament summary",
        "summary.subtitle": "Final whistle · {date}",
        "summary.pending.title": "Summary not available yet",
        "summary.pending.body": "This page unlocks the moment match #104 (the Final) is marked finished. Come back after full-time to see every pool's leaderboard and bonus predictions.",
        "summary.leaderboard": "Leaderboard",
        "summary.bonus": "Bonus predictions",
        "summary.official": "Official",
        "summary.member": "Member",
        "match.live": "Live",
        "match.live.details": "Live match details",
        "match.live.status": "Status",
        "match.live.finished": "Full time",
        "match.live.halftime": "Half-time",
        "match.live.full_time": "Score (90′)",
        "match.live.extra_time": "Extra time",
        "match.live.penalties": "Penalties",
        "match.live.phase": "Phase",
        "match.live.referee": "Referee",
        "match.live.updated_at": "Last synced:",
        "match.live.updated_seconds": "{n}s ago",
        "match.live.updated_minutes": "{n} min ago",
        "match.pool_picks": "Pool predictions",
        "match.no_pool": "You're not in any pool yet.",
        "match.create_one": "Create one",
        "match.locked_hint": "Other players' predictions become visible after kickoff.",
        "match.your_pick": "Your pick:",
        "match.no_picks": "No predictions were submitted.",
        "match.pred.title": "Your prediction",
        "match.pred.save": "Save",
        "match.pred.update": "Update",
        "match.pred.saved": "Prediction saved",
        "match.pred.saved_label": "Saved",
        "match.pen.winner": "Pen/ET winner",

        // join
        "join.title": "Joining pool…",
        "join.code": "Code",
        "join.back": "Back to dashboard",

        // countdown labels
        "cd.d": "D", "cd.h": "H", "cd.m": "M", "cd.s": "S",
        "cd.locked": "Locked",

        // pool tab — bracket
        "pool.tab.bracket": "Bracket",
        "bracket.title": "Knockout Bracket",
        "bracket.subtitle": "Predictions auto-lock at kickoff. Click any match for details.",
        "bracket.empty": "No knockout fixtures yet. Sync from API once the draw is done.",
        "bracket.your_pick": "Your pick",
        "bracket.pen_winner": "Pen/ET",
        "bracket.delete.button": "Delete predictions",
        "bracket.delete.title": "Delete knockout predictions",
        "bracket.delete.subtitle": "Choose the earliest round to clear. Earlier rounds you've already predicted stay intact. Predictions on matches that have already started are always preserved.",
        "bracket.delete.opt.r32": "All knockout (from Round of 32)",
        "bracket.delete.opt.r16": "From Round of 16",
        "bracket.delete.opt.qf": "From Quarter-finals",
        "bracket.delete.opt.sf": "From Semi-finals (incl. 3rd place)",
        "bracket.delete.opt.f": "Final only",
        "bracket.delete.from_hint": "Wipes this round and every later round.",
        "bracket.delete.warning": "This cannot be undone.",
        "bracket.delete.confirm": "Yes, delete",
        "bracket.delete.toast": "Deleted {n} knockout prediction(s) from {from}",
        "bracket.pill.r32": "R32",
        "bracket.pill.r16": "R16",
        "bracket.pill.qf": "QF",
        "bracket.pill.sf": "SF",
        "bracket.pill.f": "Final",
        "pool.quick_switch": "Switch pool",
        "bracket.round.round_of_32": "Round of 32",
        "bracket.round.round_of_16": "Round of 16",
        "bracket.round.quarter_finals": "Quarter-finals",
        "bracket.round.semi_finals": "Semi-finals",
        "bracket.round.final": "Final",
        "bracket.round.third_place": "Third place",
        "bracket.slot.winner": "Winner Group {g}",
        "bracket.slot.runner": "Runner-up Group {g}",
        "bracket.slot.third": "Best Third #{n}",
        "bracket.slot.winner_r32": "Winner R32 Match #{n}",
        "bracket.slot.winner_r16": "Winner R16 Match #{n}",
        "bracket.slot.winner_qf": "Winner QF #{n}",
        "bracket.slot.winner_sf": "Winner SF #{n}",
        "bracket.slot.loser_sf": "Loser SF #{n}",
        "bracket.best_thirds_title": "Current best-third ranking",
        "bracket.best_thirds_empty": "No third-placed teams have played yet.",
        "bracket.best_thirds_hint": "Top 8 advance to Round of 32.",

        // pool tab — group standings
        "standings.team": "Team",
        "standings.played": "MP",
        "standings.played_full": "Matches played",
        "standings.won": "W",
        "standings.won_full": "Won",
        "standings.drawn": "D",
        "standings.drawn_full": "Drawn",
        "standings.lost": "L",
        "standings.lost_full": "Lost",
        "standings.gd": "GD",
        "standings.gd_full": "Goal difference",
        "standings.points": "Pts",
        "standings.points_full": "Points",
        "standings.legend_q": "Top-2 advance",
        "standings.legend_q3": "Best-third advances",
        "groups.empty": "No group-stage fixtures yet.",
        "groups.view.real": "Actual results",
        "groups.view.predicted": "My predictions",
        "groups.view.tooltip": "Toggle between the live group tables and the tables computed from your own picks.",
        "groups.predicted.empty": "Make some group-stage predictions to see your projected tables here.",

        // notifications
        "notif.title": "Notifications",
        "notif.empty": "Nothing yet. You'll see results land here.",

        // global admin
        "gadmin.eyebrow": "Super Admin",
        "gadmin.title": "Global Control",
        "gadmin.tab.overview": "Overview",
        "gadmin.tab.users": "Users",
        "gadmin.tab.pools": "Pools",
        "gadmin.tab.log": "Sync Log",
        "gadmin.stat.users": "Users",
        "gadmin.stat.pools": "Pools",
        "gadmin.stat.matches": "Matches",
        "gadmin.stat.finished": "Finished",
        "gadmin.stat.predictions": "Predictions",
        "gadmin.users.name": "Name",
        "gadmin.users.email": "Email",
        "gadmin.users.role": "Role",
        "gadmin.users.pools": "Pools",
        "gadmin.users.picks": "Picks",
        "gadmin.users.created": "Joined",
        "gadmin.pools.admin": "Admin",
        "gadmin.pools.members": "members",
        "gadmin.pools.empty": "No pools created yet.",
        "gadmin.log.empty": "No sync activity yet.",

        // header status bar
        "status.matches": "{played}/{total} played",
        "status.matches_short": "{played}/{total}",
        "status.next": "Next kick-off",
        "status.next_short": "Next",
        "status.final": "Final",
        "status.in": "in",
        "status.now_live": "live now",
        "status.kicked_off": "kicked off",
        "status.over": "ended",
        "status.top3": "Top 3",
        "status.bonus.missing_one": "{n} bonus pick missing",
        "status.bonus.missing_many": "{n} bonus picks missing",
        "status.bonus.all_set": "All bonus picks set",
        "status.unpaid_tip": "Unpaid / Obetald",
        "status.no_pool": "Pick a pool to see ranking",
        "status.toggle_show": "Show status bar",
        "status.toggle_hide": "Hide status bar",

        // PWA install + push toggle
        "pwa.install.button": "Install",
        "pwa.install.title": "Add FIFA World Cup 2026 to your home screen",
        "push.enable": "Enable match-day push notifications",
        "push.disable": "Disable push notifications",
        "push.toast.on": "Push notifications enabled — you'll get a buzz on kickoff & results.",
        "push.toast.off": "Push notifications disabled.",

        // Magic-link login
        "auth.magic.title": "Sign in with a magic link",
        "auth.magic.subtitle": "Enter your email — we'll send you a one-click sign-in link. No password needed.",
        "auth.magic.email_ph": "you@example.com",
        "auth.magic.send": "Send magic link",
        "auth.magic.sent": "Check your inbox — link valid for 15 minutes.",
        "auth.magic.tab": "Magic link",
        "auth.magic.tab_password": "Password",
        "auth.magic.error_invalid": "Invalid or expired link. Request a new one.",
        "auth.magic.error_send": "Could not send the link. Try again or use a password.",
        "auth.magic.verifying": "Signing you in…",
        "auth.magic.success": "Welcome back! Redirecting…",

        // MVP / Best tipper history
        "pool.tab.mvp": "Hall of fame",
        "mvp.title": "Hall of fame — round MVPs",
        "mvp.subtitle": "Top scorer for each finished round across this pool.",
        "mvp.no_finished": "No rounds finished yet — check back after the first matches kick off.",
        "mvp.col.round": "Round",
        "mvp.col.matches": "Matches",
        "mvp.col.winner": "Round MVP",
        "mvp.col.points": "Pts",
        "mvp.runner_up": "Runner-up",
        "mvp.no_winner": "No predictions",
        "mvp.tied": "Tied",
        "mvp.tally": "Total MVPs",

        // Knockout phase rules (Phase 1 + 2)
        "knock.pen.title": "Wins on penalties / extra time",
        "knock.pen.choose": "Select winner…",
        "knock.info.title": "How knockout predictions work",
        "knock.info.open": "Every knockout match is open for predictions until kickoff — even when teams are still TBD.",
        "knock.info.draw": "If you predict a draw, you must also pick which team you think will advance on extra time / penalties (+2 bonus points if you're right).",
        "knock.info.tooltip": "If a slot's team is still TBD, the bracket shows the team you predicted (in italic amber, prefixed with 💭) based on your group-stage picks.",
        "knock.info.thirds": "8 of 12 third-placed teams advance to the Round of 32. We rank thirds by points → goal difference → goals scored, then apply the FIFA Annex C table to pair them with R32 slots.",
        "knock.info.scoring": "Points are awarded on the regulation (90-minute) score from football-data.org. Extra-time goals don't count toward your scoreline, but a correct penalty pick earns the +2 bonus.",
        "bracket.your_predicted": "Based on your prediction",

        // My predictions — unpicked matches
        "preds.unpicked.title": "Matches to predict",
        "preds.unpicked.empty": "All your upcoming matches are predicted. Nice.",
        "preds.unpicked.cta": "{n} match still needs a pick",
        "preds.unpicked.cta_many": "{n} matches still need picks",
        "preds.unpicked.save": "Save pick",
        "preds.unpicked.saved": "Pick saved",
        "preds.unpicked.locked": "Locked — kicked off",
        "preds.picked.title": "Already predicted",
        "preds.search_ph": "Search team or round…",

        // pool — friendly bet (visible to all members)
        "pool.bet.title": "Friendly bet",
        "pool.bet.eyebrow": "STAKE",
        "pool.bet.stake": "Stake",
        "pool.bet.winner": "Who wins",
        "pool.bet.instructions": "How to pay",
        "pool.bet.welcome": "Welcome! Here's the bet for this pool.",
        "pool.bet.dismiss": "Got it",
        "pool.bet.edit": "Edit bet",
        "pool.bet.save": "Save bet",
        "pool.bet.none.admin": "No friendly bet set. Click 'Edit bet' to add one.",
        "pool.bet.toast.saved": "Friendly bet updated",
        "pool.bet.my_paid": "You're marked as paid",
        "pool.bet.my_unpaid": "Reminder: your stake hasn't been marked as paid yet.",
        "pool.bet.my_unpaid_hint": "Follow the payment instructions above, then ask the admin to tick you off.",

        // pool — members & paid tracking
        "pool.members.title": "Members & payment",
        "pool.members.hint_admin": "Tick the box once a member has paid their stake.",
        "pool.members.hint_member": "The admin will tick you off once your stake is paid.",
        "pool.members.paid": "Paid",
        "pool.members.unpaid": "Unpaid",
        "pool.members.you": "you",
        "pool.members.admin_tag": "admin",
        "pool.members.toast.paid": "{name} marked as paid",
        "pool.members.toast.unpaid": "{name} marked as unpaid",
        "pool.members.toast.removed": "{name} removed from pool",
        "pool.members.remove.title": "Remove member",
        "pool.members.remove.confirm": "Remove {name} from this pool? Their predictions in this pool will also be deleted.",
        "pool.members.remove.warning": "This can't be undone.",
        "pool.members.remove.button": "Remove",
        "pool.members.email_btn": "Send email",
        "pool.members.email_all": "Email all members",

        // pool — admin actions
        "pool.tab.bet": "Bet & members",
        "pool.tab.bonus": "Bonus",
        "pool.admin.danger.title": "Danger zone",
        "pool.admin.danger.desc": "Deleting the pool wipes all predictions, bonus picks, payment status and leaderboard data for everyone in it. There is no undo.",
        "pool.admin.delete.title": "Delete this pool?",
        "pool.admin.delete.confirm": "Permanently delete the pool \"{name}\"?",
        "pool.admin.delete.warning": "All predictions, bonus picks, payment status and scores will be lost forever.",
        "pool.admin.delete.button": "Delete pool",
        "pool.admin.delete.toast": "Pool deleted",

        // leaderboard columns
        "leader.col.player": "Player",
        "leader.col.match": "Match",
        "leader.col.bonus": "Bonus",
        "leader.col.total": "Total",

        // bonus predictions
        "bonus.title": "Bonus predictions",
        "bonus.subtitle": "Six one-off picks for the whole tournament — up to 100 extra points.",
        "bonus.my.title": "My picks",
        "bonus.save": "Save picks",
        "bonus.saving": "Saving…",
        "bonus.saved": "Bonus picks saved",
        "bonus.points_earned": "Points earned",
        "bonus.pending_short": "Pending",
        "bonus.hit_short": "✓ Hit",
        "bonus.miss_short": "Miss",
        "bonus.row.top_scorer.label": "Best goal scorer",
        "bonus.row.top_scorer.points": "20 p",
        "bonus.row.top_scorer.ph": "Player name (e.g. Erling Haaland)",
        "bonus.row.gold.label": "Gold — winner",
        "bonus.row.gold.points": "20 p",
        "bonus.row.silver.label": "Silver — runner-up",
        "bonus.row.silver.points": "20 p",
        "bonus.row.bronze.label": "Bronze — 3rd place",
        "bonus.row.bronze.points": "20 p",
        "bonus.row.most_goals.label": "Most goals — group stage",
        "bonus.row.most_goals.points": "10 p",
        "bonus.row.most_goals.hint": "Auto-resolved from finished group-stage matches.",
        "bonus.row.most_conceded.label": "Most conceded — group stage",
        "bonus.row.most_conceded.points": "10 p",
        "bonus.row.most_conceded.hint": "Auto-resolved from finished group-stage matches.",
        "bonus.team.placeholder": "— select team —",
        "bonus.answers.title": "Correct answers (admin)",
        "bonus.answers.hint_top_scorer": "Set the top-scorer name when the tournament ends. Predictions are matched leniently (case-insensitive + small typos OK). If two or more players tie for the golden boot, list all names separated by commas (e.g. \"Messi, Haaland\") — a user's guess scores if it matches ANY of them. Gold, silver and bronze auto-set from finished knockout matches.",
        "bonus.resolved.title": "Resolved answers",
        "bonus.resolved.hint": "Auto-derived from the live results. For Most goals / Most conceded, all tied teams are listed and any match counts as a hit.",
        "bonus.answers.hint": "Set after the final game. Top scorer + podium are scored when set; group-stage stats resolve automatically as group results come in.",
        "bonus.answers.save": "Save answers",
        "bonus.answers.saved": "Bonus answers saved",
        "bonus.answers.auto_current": "Currently leading (group stage):",
        "bonus.answers.not_set": "Not set yet",
        "bonus.locked.title": "Bonus answers locked",
        "bonus.locked.body": "The tournament has kicked off (first match at {date}). Your bonus picks are now frozen and will be scored as the matches play out.",

        // profile
        "nav.profile": "Profile",
        "profile.title": "My profile",
        "profile.subtitle": "Update your details, manage pool memberships, or close your account.",
        "profile.name": "Display name",
        "profile.email": "Email",
        "profile.save": "Save",
        "profile.identity.title": "Identity",
        "profile.identity.saved": "Profile updated",
        "profile.identity.no_change": "Nothing to save.",
        "profile.email.password_label": "Confirm current password",
        "profile.email.password_placeholder": "Required to change email",
        "profile.email.hint": "Changing your email requires your current password.",
        "profile.email.need_password": "Enter your current password to change email.",
        "profile.password.title": "Change password",
        "profile.password.current": "Current password",
        "profile.password.new": "New password",
        "profile.password.confirm": "Confirm new password",
        "profile.password.save": "Update password",
        "profile.password.saved": "Password updated",
        "profile.password.mismatch": "New passwords don't match.",
        "profile.password.too_short": "New password must be at least 6 characters.",
        "profile.pools.title": "Pool memberships",
        "profile.pools.empty": "You're not in any pools yet.",
        "profile.pools.member": "Member",
        "profile.pools.you_admin": "You're the admin",
        "profile.pools.leave": "Leave",
        "profile.pools.admin_cannot_leave": "Pool admins can't leave. Hand off admin or delete the pool from the pool page.",
        "profile.leave.title": "Leave this pool?",
        "profile.leave.body1": "Leaving {name} removes your membership and deletes your predictions and bonus answers in this pool.",
        "profile.leave.body2": "This cannot be undone.",
        "profile.leave.confirm": "Yes, leave pool",
        "profile.leave.done": "Left pool · {n} prediction(s) removed",
        "profile.danger.title": "Danger zone",
        "profile.danger.body": "Deleting your account anonymizes your data but keeps historical predictions intact so other players' leaderboards stay accurate.",
        "profile.delete.password_label": "Confirm current password",
        "profile.delete.button": "Delete account",
        "profile.delete.title": "Delete your account?",
        "profile.delete.body1": "Your name will be replaced with 'Deleted user' and your email anonymized. Predictions stay so other players' scores are preserved.",
        "profile.delete.body2": "You will be logged out and unable to sign in again with this account.",
        "profile.delete.confirm": "Yes, delete my account",
        "profile.delete.done": "Account deleted. Goodbye!",
    },

    sv: {
        // nav / layout
        "nav.dashboard": "Översikt",
        "nav.help": "Hjälp",
        "quickrules.title": "Så funkar det",
        "quickrules.predict.t": "Tippa alla matcher",
        "quickrules.predict.d": "Välj resultat för varje match. Poäng från gruppspel och slutspel räknas separat.",
        "quickrules.lock.t": "Låses vid avspark",
        "quickrules.lock.d": "När matchen sparkar igång låses tipset. Även 0–0 måste sparas innan dess.",
        "quickrules.bonus.t": "Bonuspoäng",
        "quickrules.bonus.d": "Skyttekung, guld/silver/brons, flest gjorda/insläppta — hämtas automatiskt från resultaten.",
        "quickrules.cta.create": "Skapa eget gäng",
        "quickrules.cta.join": "Gå med via kod",
        "quickrules.cta.full": "Alla regler →",
        "quickrules.contact": "Kontakta utvecklaren",
        "nav.signin": "Logga in",
        "nav.admin_badge": "Admin",
        "layout.footer": "Ett privat VM-tips bara för vänner",
        "lang.label": "Språk",
        "lang.en": "EN",
        "lang.sv": "SV",

        // landing
        "landing.eyebrow": "Privat VM-tips 2026",
        "landing.title_1": "Tippa",
        "landing.title_emph": "exakt",
        "landing.title_2": "resultat.",
        "landing.title_action": "Krossa",
        "landing.title_3": "dina vänner.",
        "landing.subtitle": "Skapa ett privat tipsgäng, bjud in dina vänner och kör tipset för varje match i VM 2026. {home} för rätt antal mål per lag + {outcome} för rätt tecken (upp till {max} per gruppmatch), {knock} extra för rätt straff-/förlängningsvinnare i slutspelet, plus {bonus} bonuspoäng för skyttekung, guld/silver/brons och mest gjorda/insläppta mål.",
        "landing.subtitle.home": "2 p",
        "landing.subtitle.outcome": "3 p",
        "landing.subtitle.max": "7 max",
        "landing.subtitle.knock": "+2 p",
        "landing.subtitle.bonus": "100",
        "landing.cta.create": "Skapa ditt gäng",
        "landing.cta.open": "Öppna översikten",
        "landing.cta.have_account": "Logga in (eller skapa konto)",
        "landing.stat.teams": "{n} lag",
        "landing.stat.matches": "{n} matcher i turneringen",
        "landing.stat.bragging": "Skapad av ola.wollin@gmail.com med [[L1]]app.emergent.sh[[/L1]] · resultat från [[L2]]football-data.org[[/L2]]",
        "landing.scoring.eyebrow": "Den svenska 7-poängsregeln",
        "landing.scoring.title_1": "En match.",
        "landing.scoring.title_2": "Tre sätt att få poäng.",
        "landing.scoring.home.title": "Rätt antal mål",
        "landing.scoring.home.desc": "Du prickade exakt antal mål.",
        "landing.scoring.away.title": "Rätt bortamål",
        "landing.scoring.away.desc": "Du prickade exakt antal bortamål.",
        "landing.scoring.outcome.title": "Rätt tecken",
        "landing.scoring.outcome.desc": "Hemmavinst, oavgjort eller bortavinst — rätt gissning.",
        "landing.scoring.knock.title": "Slutspel: straff/förlängning",
        "landing.scoring.knock.desc": "Bonus när du även tippar rätt lag som går vidare efter straffar eller förlängning.",
        "landing.scoring.bonus.title": "Bonusfrågor",
        "landing.scoring.bonus.desc": "Skyttekung (20) + guld/silver/brons (20 vardera) + flest mål & flest insläppta (10 vardera) = upp till 100 p.",
        "landing.scoring.max": "Max per match",
        "landing.feature.private.title": "Privata tipsgäng",
        "landing.feature.private.desc": "Inbjudan via 6-teckenskod. Dina kompisar, era regler.",
        "landing.feature.lock.title": "Auto-låsning",
        "landing.feature.lock.desc": "Tipsen låses vid avspark. Ingen tjuvkikar.",
        "landing.feature.leader.title": "Liverankning",
        "landing.feature.leader.desc": "Poängen räknas om så fort ett resultat kommer in.",
        "landing.final.title_1": "Redo att",
        "landing.final.title_2": "tippa igång?",
        "landing.final.desc": "Skapa ett privat gäng på sekunder. Dela koden. Låt tipsandet börja.",

        // auth
        "auth.login.title": "Logga in",
        "auth.login.subtitle": "Välkommen tillbaka till tipset.",
        "auth.email": "E-post",
        "auth.password": "Lösenord",
        "auth.no_account": "Inget konto?",
        "auth.create_one": "Skapa ett",
        "auth.have_account": "Har du redan ett konto?",
        "auth.register.title": "Gå med i ligan",
        "auth.register.subtitle": "Skapa ett konto på 10 sekunder.",
        "auth.name": "Användarnamn",
        "auth.register.submit": "Skapa konto",

        // dashboard
        "dash.eyebrow": "Dina tipsgäng",
        "dash.title": "Översikt",
        "dash.btn.join": "Gå med",
        "dash.btn.create": "Skapa gäng",
        "dash.empty.title": "Inga tipsgäng ännu",
        "dash.empty.desc": "Starta ditt första privata gäng eller gå med i ett via en inbjudningskod från en vän.",
        "dash.members_count": "{n} medlemmar",
        "dash.modal.new": "Nytt tipsgäng",
        "dash.modal.name": "Namn",
        "dash.modal.desc": "Beskrivning (valfritt)",
        "dash.modal.cancel": "Avbryt",
        "dash.modal.create": "Skapa",
        "dash.modal.creating": "Skapar…",
        "dash.join.title": "Gå med i tipsgäng",
        "dash.join.code": "Inbjudningskod",
        "dash.join.joining": "Går med…",
        "dash.join.submit": "Gå med",
        "dash.toast.pool_created": "Tipsgäng skapat · kod {code}",
        "dash.toast.joined": "Du gick med i {name}",

        // dashboard create modal — friendly bet
        "dash.modal.bet.title": "Vänskaplig insats (valfritt)",
        "dash.modal.bet.hint": "Sätt insatsen och reglerna. Nya medlemmar ser detta när de går med.",
        "dash.modal.bet.amount": "Belopp",
        "dash.modal.bet.currency": "Valuta",
        "dash.modal.bet.winner": "Vem vinner?",
        "dash.modal.bet.winner_ph": "T.ex. Den som toppar rankningen vid slutsignalen.",
        "dash.modal.bet.instructions": "Hur man betalar / instruktioner",
        "dash.modal.bet.instructions_ph": "T.ex. Swisha 0701234567 innan första matchen.",

        // pool detail
        "pool.back_dash": "← Översikt",
        "pool.players_one": "{n} spelare",
        "pool.players_other": "{n} spelare",
        "pool.you_admin": "Du är admin",
        "pool.invite_code": "Inbjudningskod",
        "pool.copy_code": "Kopiera kod",
        "pool.copy_link": "Kopiera inbjudningslänk",
        "pool.tab.fixtures": "Matcher",
        "pool.tab.groups": "Grupper",
        "pool.tab.predictions": "Mina tips",
        "pool.tab.leaderboard": "Rankning",
        "pool.tab.admin": "Admin",
        "pool.toast.code_copied": "Inbjudningskod kopierad",
        "pool.toast.link_copied": "Inbjudningslänk kopierad",
        "pool.toast.pred_saved": "Tips sparat",
        "pool.toast.recalc": "Rankning omräknad",

        // fixtures view
        "fix.search": "Sök lag eller omgång…",
        "fix.filter.all": "alla",
        "fix.filter.upcoming": "kommande",
        "fix.filter.locked": "låsta",
        "fix.empty": "Inga matcher matchar ditt filter.",
        "fix.expand_all": "Expandera alla",
        "fix.collapse_all": "Fäll ihop alla",
        "fix.expand_next": "Nästa 2 matcher",
        "fix.sort_label": "Sortera",
        "fix.sort_kickoff": "Avspark",
        "fix.sort_groups": "Grupper",
        "fix.save_all": "Spara alla ({n})",
        "pool.prize_pot": "Total pott",
        "leader.group_pts": "Grupp",
        "leader.knock_pts": "Slutspel",
        "fix.locked": "Låst",
        "fix.details": "Detaljer →",
        "fix.save": "Spara",
        "fix.saved": "Sparat",
        "fix.update": "Uppdatera",
        "fix.points": "+{n} p",
        "fix.pts_short": "p",
        "fix.predicted_short": "Tipp",
        "fix.no_pred_short": "— Inget tips —",
        "fix.save_all.prompt.title": "Spara alla ändringar?",
        "fix.save_all.prompt.body": "Du har {n} osparade tips på flera matcher. Spara alla i ett svep, eller bara den du klickade på?",
        "fix.save_all.prompt.just_this": "Bara denna",
        "fix.save_all.prompt.all": "Spara alla ({n})",

        // predictions view
        "preds.empty": "Du har inte tippat något ännu. Gå till fliken Matcher för att börja tippa.",
        "preds.empty_for_pool": "Inga tips i detta gäng ännu. Tippa via Matcher- eller Slutspelsfliken i gänget.",
        "preds.pool_label": "Gäng",
        "preds.copy.button": "Kopiera till annat gäng",
        "preds.copy.title": "Kopiera dina tips",
        "preds.copy.warning": "Detta SKRIVER ÖVER alla tips du redan har i målet med motsvarande tips från detta gäng. Matcher som redan startat i målet hoppas över.",
        "preds.copy.dest_label": "Målgäng",
        "preds.copy.choose_pool": "Välj gäng…",
        "preds.copy.confirm": "Kopiera & skriv över",
        "preds.copy.result": "Kopierade: {c} · Överskrivna: {o} · Låsta (hoppade): {s}",
        "common.cancel": "Avbryt",
        "preds.label.pick": "Tips",
        "preds.label.result": "Resultat",
        "preds.locked": "Låst",
        "preds.open": "Öppet",

        // leaderboard
        "leader.empty.title": "Inga poäng än. Dyker upp så fort resultaten kommer in.",
        "leader.empty.force": "Tvinga omräkning",
        "leader.title": "Rankning",
        "leader.refresh": "Uppdatera",
        "leader.recalc": "Räkna om",
        "leader.summary.title": "Ligasammanfattning (bild)",
        "leader.summary.subtitle": "Ladda ner en delbar PNG med pallen, prispotten & alla spelares poäng",
        "leader.summary.cta": "Öppna",
        "leader.pts": "p",
        "leader.csv": "Exportera CSV",
        "leader.mvp": "MVP",
        "leader.mvp_round": "MVP — {round}",
        "leader.climber": "Största klättraren",
        "leader.dropper": "Största fallet",
        "leader.you": "DU",

        // admin
        "admin.scheduler.fixtures.title": "Matchsynk · var {n} min",
        "admin.scheduler.on": "På",
        "admin.scheduler.off": "Av",
        "admin.scheduler.next": "Nästa körning:",
        "admin.scheduler.last": "Senast:",
        "admin.scheduler.summary": "{updated} uppdaterade · {changed} nya",
        "admin.scheduler.refresh": "Uppdatera",
        "admin.scheduler.run_now": "Kör nu",
        "admin.scheduler.syncing": "Synkar…",
        "admin.live.title": "Live-poll · var {n} min",
        "admin.live.match_live": "Match Live",
        "admin.live.idle": "Vilande",
        "admin.live.polling_desc": "Hämtar /fixtures?live=all för pågående matchresultat.",
        "admin.live.skip_desc": "Hoppar över API-anrop — ingen match i avsparksfönstret.",
        "admin.live.next_check": "Nästa koll:",
        "admin.live.last": "Senast:",
        "admin.live.summary": "{updated} uppdaterade · {changed} ändringar",
        "admin.live.poll_now": "Polla nu",
        "admin.live.polling": "Pollar…",
        "admin.live.toast.skipped": "Inget aktivt matchfönster — live-poll hoppades över",
        "admin.live.toast.ok": "Live-poll · {updated} matcher kontrollerade · {changed} ändringar",
        "admin.sync.teams.title": "Synka lag",
        "admin.sync.teams.desc": "Hämta lag från API-Football",
        "admin.sync.fixtures.title": "Synka matcher + resultat",
        "admin.sync.fixtures.desc": "Importera matcher & senaste resultat",
        "admin.recalc.title": "Räkna om poäng",
        "admin.recalc.desc": "Bygg om detta gängs rankning",
        "admin.edit.title": "Ändra resultat manuellt",
        "admin.search": "Sök lag eller omgång…",
        "admin.edit_btn": "Ändra",
        "admin.modal.set_result": "Sätt resultat",
        "admin.modal.cancel": "Avbryt",
        "admin.modal.save": "Spara",
        "admin.modal.penalty_title": "Vinnare på straffar",
        "admin.modal.penalty_hint": "Matchen slutade lika. Välj laget som vann på straffar så slutspelsträdet uppdateras korrekt.",
        "admin.toast.sync_ok": "{kind} synkat",
        "admin.toast.result_saved": "Resultat sparat + rankning omräknad",
        "admin.toast.synced_full": "Synkat · {updated} uppdaterade · {changed} resultat ändrade",

        // match detail
        "match.back": "← Tillbaka",
        "match.number.label": "Match nr",
        "summary.title.eyebrow": "Säsongen i sammandrag",
        "summary.title": "Turneringssammanfattning",
        "summary.subtitle": "Slutsignal · {date}",
        "summary.pending.title": "Sammanfattning inte tillgänglig än",
        "summary.pending.body": "Sidan låses upp när match #104 (finalen) markerats som avslutad. Kom tillbaka efter slutsignal för att se varje gängs topplista och bonustips.",
        "summary.leaderboard": "Topplista",
        "summary.bonus": "Bonustips",
        "summary.official": "Officiellt",
        "summary.member": "Medlem",
        "match.live": "Direkt",
        "match.live.details": "Live-matchinfo",
        "match.live.status": "Status",
        "match.live.finished": "Slutsignal",
        "match.live.halftime": "Halvtid",
        "match.live.full_time": "Slutresultat (90′)",
        "match.live.extra_time": "Förlängning",
        "match.live.penalties": "Straffar",
        "match.live.phase": "Fas",
        "match.live.referee": "Domare",
        "match.live.updated_at": "Senast synkad:",
        "match.live.updated_seconds": "{n}s sedan",
        "match.live.updated_minutes": "{n} min sedan",
        "match.pool_picks": "Tips i tipsgänget",
        "match.no_pool": "Du är inte med i något tipsgäng ännu.",
        "match.create_one": "Skapa ett",
        "match.locked_hint": "Andra spelares tips visas först efter avspark.",
        "match.your_pick": "Ditt tips:",
        "match.no_picks": "Inga tips lades.",
        "match.pred.title": "Ditt tips",
        "match.pred.save": "Spara",
        "match.pred.update": "Uppdatera",
        "match.pred.saved": "Tips sparat",
        "match.pred.saved_label": "Sparat",
        "match.pen.winner": "Vinnare på straff/förlängning",

        // join
        "join.title": "Går med i tipsgäng…",
        "join.code": "Kod",
        "join.back": "Tillbaka till översikten",

        // countdown labels
        "cd.d": "D", "cd.h": "T", "cd.m": "M", "cd.s": "S",
        "cd.locked": "Låst",

        // pool tab — bracket
        "pool.tab.bracket": "Slutspel",
        "bracket.title": "Slutspelsträd",
        "bracket.subtitle": "Tipsen låses vid avspark. Klicka på en match för detaljer.",
        "bracket.empty": "Inga slutspelsmatcher än. Synka från API:t när lottningen är klar.",
        "bracket.your_pick": "Ditt tips",
        "bracket.pen_winner": "Straff/förl.",
        "bracket.delete.button": "Radera tips",
        "bracket.delete.title": "Radera slutspels-tips",
        "bracket.delete.subtitle": "Välj från vilken omgång det ska rensas. Tidigare omgångar du redan tippat behålls. Tips på matcher som redan startat rörs aldrig.",
        "bracket.delete.opt.r32": "Allt slutspel (från 32-delsfinal)",
        "bracket.delete.opt.r16": "Från åttondelsfinal",
        "bracket.delete.opt.qf": "Från kvartsfinal",
        "bracket.delete.opt.sf": "Från semifinal (inkl. tredjepris)",
        "bracket.delete.opt.f": "Endast final",
        "bracket.delete.from_hint": "Raderar denna omgång och alla senare omgångar.",
        "bracket.delete.warning": "Detta går inte att ångra.",
        "bracket.delete.confirm": "Ja, radera",
        "bracket.delete.toast": "Raderade {n} slutspels-tips från {from}",
        "bracket.pill.r32": "32-del",
        "bracket.pill.r16": "Åttondel",
        "bracket.pill.qf": "Kvart",
        "bracket.pill.sf": "Semi",
        "bracket.pill.f": "Final",
        "pool.quick_switch": "Byt tipsgäng",
        "bracket.round.round_of_32": "32-delsfinal",
        "bracket.round.round_of_16": "Åttondelsfinal",
        "bracket.round.quarter_finals": "Kvartsfinal",
        "bracket.round.semi_finals": "Semifinal",
        "bracket.round.final": "Final",
        "bracket.round.third_place": "Tredje plats",
        "bracket.slot.winner": "Vinnare Grupp {g}",
        "bracket.slot.runner": "Tvåa Grupp {g}",
        "bracket.slot.third": "Bästa trea #{n}",
        "bracket.slot.winner_r32": "Vinnare R32 Match #{n}",
        "bracket.slot.winner_r16": "Vinnare R16 Match #{n}",
        "bracket.slot.winner_qf": "Vinnare KF #{n}",
        "bracket.slot.winner_sf": "Vinnare SF #{n}",
        "bracket.slot.loser_sf": "Förlorare SF #{n}",
        "bracket.best_thirds_title": "Aktuell trea-rankning",
        "bracket.best_thirds_empty": "Inga treor har spelat än.",
        "bracket.best_thirds_hint": "Topp 8 går vidare till åttondelsfinal.",

        // pool tab — group standings
        "standings.team": "Lag",
        "standings.played": "M",
        "standings.played_full": "Matcher spelade",
        "standings.won": "V",
        "standings.won_full": "Vunna",
        "standings.drawn": "O",
        "standings.drawn_full": "Oavgjorda",
        "standings.lost": "F",
        "standings.lost_full": "Förlorade",
        "standings.gd": "MS",
        "standings.gd_full": "Målskillnad",
        "standings.points": "P",
        "standings.points_full": "Poäng",
        "standings.legend_q": "Topp-2 går vidare",
        "standings.legend_q3": "Bästa trea går vidare",
        "groups.empty": "Inga gruppspelsmatcher än.",
        "groups.view.real": "Verkliga resultat",
        "groups.view.predicted": "Mina tips",
        "groups.view.tooltip": "Växla mellan live-tabellerna och tabeller baserade på dina egna tips.",
        "groups.predicted.empty": "Tippa några gruppspelsmatcher för att se din prognos här.",

        // notifications
        "notif.title": "Notifieringar",
        "notif.empty": "Inget än. Resultat dyker upp här.",

        // global admin
        "gadmin.eyebrow": "Superadmin",
        "gadmin.title": "Global kontroll",
        "gadmin.tab.overview": "Översikt",
        "gadmin.tab.users": "Användare",
        "gadmin.tab.pools": "Tipsgäng",
        "gadmin.tab.log": "Synklogg",
        "gadmin.stat.users": "Användare",
        "gadmin.stat.pools": "Tipsgäng",
        "gadmin.stat.matches": "Matcher",
        "gadmin.stat.finished": "Färdiga",
        "gadmin.stat.predictions": "Tips",
        "gadmin.users.name": "Namn",
        "gadmin.users.email": "E-post",
        "gadmin.users.role": "Roll",
        "gadmin.users.pools": "Gäng",
        "gadmin.users.picks": "Tips",
        "gadmin.users.created": "Gick med",
        "gadmin.pools.admin": "Admin",
        "gadmin.pools.members": "medlemmar",
        "gadmin.pools.empty": "Inga tipsgäng skapade än.",
        "gadmin.log.empty": "Ingen synkaktivitet än.",

        // header status bar
        "status.matches": "{played}/{total} spelade",
        "status.matches_short": "{played}/{total}",
        "status.next": "Nästa avspark",
        "status.next_short": "Nästa",
        "status.final": "Final",
        "status.in": "om",
        "status.now_live": "spelas nu",
        "status.kicked_off": "har börjat",
        "status.over": "avslutad",
        "status.top3": "Topp 3",
        "status.bonus.missing_one": "{n} bonustips saknas",
        "status.bonus.missing_many": "{n} bonustips saknas",
        "status.bonus.all_set": "Alla bonustips ifyllda",
        "status.unpaid_tip": "Obetald / Unpaid",
        "status.no_pool": "Välj ett tipsgäng för rankning",
        "status.toggle_show": "Visa statusrad",
        "status.toggle_hide": "Dölj statusrad",

        // PWA install + push toggle
        "pwa.install.button": "Installera",
        "pwa.install.title": "Lägg till FIFA World Cup 2026 på din startskärm",
        "push.enable": "Slå på push-notiser på matchdag",
        "push.disable": "Slå av push-notiser",
        "push.toast.on": "Push-notiser aktiverade — du får en pling vid avspark och resultat.",
        "push.toast.off": "Push-notiser avstängda.",

        // Magic-link login
        "auth.magic.title": "Logga in med en magisk länk",
        "auth.magic.subtitle": "Skriv din e-post — vi skickar en länk du kan klicka på. Inget lösenord behövs.",
        "auth.magic.email_ph": "du@example.com",
        "auth.magic.send": "Skicka magisk länk",
        "auth.magic.sent": "Kolla din inkorg — länken gäller i 15 minuter.",
        "auth.magic.tab": "Magisk länk",
        "auth.magic.tab_password": "Lösenord",
        "auth.magic.error_invalid": "Ogiltig eller utgången länk. Begär en ny.",
        "auth.magic.error_send": "Kunde inte skicka länken. Försök igen eller använd lösenord.",
        "auth.magic.verifying": "Loggar in dig…",
        "auth.magic.success": "Välkommen tillbaka! Omdirigerar…",

        // MVP / Best tipper history
        "pool.tab.mvp": "Hall of fame",
        "mvp.title": "Hall of fame — rundans MVP",
        "mvp.subtitle": "Bästa tippare för varje avslutad omgång i den här poolen.",
        "mvp.no_finished": "Inga rundor klara ännu — kom tillbaka efter första avsparken.",
        "mvp.col.round": "Omgång",
        "mvp.col.matches": "Matcher",
        "mvp.col.winner": "Rundans MVP",
        "mvp.col.points": "Pts",
        "mvp.runner_up": "Andraplats",
        "mvp.no_winner": "Inga tips",
        "mvp.tied": "Delat",
        "mvp.tally": "Totalt antal MVP",

        // Knockout phase rules
        "knock.pen.title": "Vinner på straffar / förlängning",
        "knock.pen.choose": "Välj vinnare…",
        "knock.info.title": "Så fungerar slutspelstipset",
        "knock.info.open": "Alla slutspelsmatcher är öppna för tips ända fram till avspark — även när lagen fortfarande är okända (TBD).",
        "knock.info.draw": "Om du tippar oavgjort måste du också välja vilket lag du tror går vidare efter förlängning / straffar (+2 bonuspoäng om du har rätt).",
        "knock.info.tooltip": "Om en plats fortfarande är TBD visar slutspelsträdet det lag du tippade fram baserat på dina gruppspels-tips.",
        "knock.info.thirds": "8 av 12 trean-lagen går vidare till åttondelarna. Vi rangordnar treorna efter poäng → målskillnad → gjorda mål, och tillämpar sedan FIFA:s Annex C-tabell för att para ihop dem med åttondelsplatser.",
        "knock.info.scoring": "Poäng räknas på ställningen efter full ordinarie tid (90 minuter) från football-data.org. Mål i förlängning räknas inte mot ditt tippade resultat, men ett rätt straff-val ger +2 bonus.",
        "bracket.your_predicted": "Baserat på ditt tips",

        // My predictions — unpicked matches
        "preds.unpicked.title": "Matcher att tippa",
        "preds.unpicked.empty": "Du har tippat alla kommande matcher. Snyggt.",
        "preds.unpicked.cta": "{n} match väntar på tips",
        "preds.unpicked.cta_many": "{n} matcher väntar på tips",
        "preds.unpicked.save": "Spara tips",
        "preds.unpicked.saved": "Tips sparat",
        "preds.unpicked.locked": "Låst — avspark gjord",
        "preds.picked.title": "Redan tippade",
        "preds.search_ph": "Sök lag eller omgång…",

        // pool — friendly bet (visible to all members)
        "pool.bet.title": "Vänskaplig insats",
        "pool.bet.eyebrow": "INSATS",
        "pool.bet.stake": "Insats",
        "pool.bet.winner": "Vem vinner",
        "pool.bet.instructions": "Så betalar du",
        "pool.bet.welcome": "Välkommen! Här är insatsen för det här tipsgänget.",
        "pool.bet.dismiss": "Uppfattat",
        "pool.bet.edit": "Ändra insats",
        "pool.bet.save": "Spara",
        "pool.bet.none.admin": "Ingen vänskaplig insats satt. Klicka 'Ändra insats' för att lägga till.",
        "pool.bet.toast.saved": "Vänskaplig insats uppdaterad",
        "pool.bet.my_paid": "Du är markerad som betald",
        "pool.bet.my_unpaid": "Påminnelse: din insats är inte markerad som betald än.",
        "pool.bet.my_unpaid_hint": "Följ betalningsinstruktionerna ovan och be admin att bocka av dig.",

        // pool — members & paid tracking
        "pool.members.title": "Medlemmar & betalning",
        "pool.members.hint_admin": "Bocka av rutan när en medlem har betalat sin insats.",
        "pool.members.hint_member": "Admin bockar av dig när din insats är betald.",
        "pool.members.paid": "Betald",
        "pool.members.unpaid": "Obetald",
        "pool.members.you": "du",
        "pool.members.admin_tag": "admin",
        "pool.members.toast.paid": "{name} markerad som betald",
        "pool.members.toast.unpaid": "{name} markerad som obetald",
        "pool.members.toast.removed": "{name} togs bort från tipsgänget",
        "pool.members.remove.title": "Ta bort medlem",
        "pool.members.remove.confirm": "Ta bort {name} från tipsgänget? Personens tips i gänget raderas också.",
        "pool.members.remove.warning": "Detta kan inte ångras.",
        "pool.members.remove.button": "Ta bort",
        "pool.members.email_btn": "Skicka e-post",
        "pool.members.email_all": "Mejla alla medlemmar",

        // pool — admin actions
        "pool.tab.bet": "Insats & medlemmar",
        "pool.tab.bonus": "Bonus",
        "pool.admin.danger.title": "Riskzon",
        "pool.admin.danger.desc": "Raderar tipsgänget och alla tips, bonus, betalstatus och rankning för alla. Det går inte att ångra.",
        "pool.admin.delete.title": "Radera tipsgänget?",
        "pool.admin.delete.confirm": "Radera tipsgänget \"{name}\" permanent?",
        "pool.admin.delete.warning": "Alla tips, bonus, betalstatus och poäng försvinner för gott.",
        "pool.admin.delete.button": "Radera tipsgäng",
        "pool.admin.delete.toast": "Tipsgänget raderat",

        // leaderboard columns
        "leader.col.player": "Spelare",
        "leader.col.match": "Match",
        "leader.col.bonus": "Bonus",
        "leader.col.total": "Totalt",

        // bonus predictions
        "bonus.title": "Bonustips",
        "bonus.subtitle": "Sex engångsval för hela turneringen — upp till 100 extrapoäng.",
        "bonus.my.title": "Mina val",
        "bonus.save": "Spara val",
        "bonus.saving": "Sparar…",
        "bonus.saved": "Bonusval sparade",
        "bonus.points_earned": "Intjänade poäng",
        "bonus.pending_short": "Avgörs senare",
        "bonus.hit_short": "✓ Rätt",
        "bonus.miss_short": "Fel",
        "bonus.row.top_scorer.label": "Skytteligavinnare",
        "bonus.row.top_scorer.points": "20 p",
        "bonus.row.top_scorer.ph": "Spelarnamn (t.ex. Erling Haaland)",
        "bonus.row.gold.label": "Guld — vinnare",
        "bonus.row.gold.points": "20 p",
        "bonus.row.silver.label": "Silver — tvåa",
        "bonus.row.silver.points": "20 p",
        "bonus.row.bronze.label": "Brons — trea",
        "bonus.row.bronze.points": "20 p",
        "bonus.row.most_goals.label": "Flest gjorda mål — gruppspel",
        "bonus.row.most_goals.points": "10 p",
        "bonus.row.most_goals.hint": "Avgörs automatiskt från avslutade gruppspelsmatcher.",
        "bonus.row.most_conceded.label": "Flest insläppta mål — gruppspel",
        "bonus.row.most_conceded.points": "10 p",
        "bonus.row.most_conceded.hint": "Avgörs automatiskt från avslutade gruppspelsmatcher.",
        "bonus.team.placeholder": "— välj lag —",
        "bonus.answers.title": "Rätt svar (admin)",
        "bonus.answers.hint_top_scorer": "Sätt skyttekungens namn när turneringen är klar. Tips matchas tolerant (gemener/versaler + små stavfel ok). Om två eller flera spelare delar skytteligan, ange alla namn separerade med komma (t.ex. \"Messi, Haaland\") — en spelares gissning ger poäng om den matchar NÅGOT av namnen. Guld, silver och brons sätts automatiskt från färdigspelade slutspelsmatcher.",
        "bonus.resolved.title": "Rätta svar",
        "bonus.resolved.hint": "Automatiskt härlett från live-resultaten. För 'Flest gjorda mål' och 'Flest insläppta mål' listas alla lag som delar förstaplatsen — vilket som helst räknas som rätt.",
        "bonus.answers.hint": "Sätt efter sista matchen. Skyttekung + medaljer ger poäng när du sätter dem; gruppspelsstatistik avgörs automatiskt när matcherna spelas.",
        "bonus.answers.save": "Spara svar",
        "bonus.answers.saved": "Bonussvar sparade",
        "bonus.answers.auto_current": "Leder just nu (gruppspel):",
        "bonus.answers.not_set": "Inte satt än",
        "bonus.locked.title": "Bonusfrågor låsta",
        "bonus.locked.body": "Turneringen har börjat (första matchen {date}). Dina bonustips är nu frysta och poängsätts allt eftersom matcherna spelas.",

        // profile
        "nav.profile": "Profil",
        "profile.title": "Min profil",
        "profile.subtitle": "Uppdatera dina uppgifter, hantera tipsgäng eller stäng kontot.",
        "profile.name": "Visningsnamn",
        "profile.email": "E-post",
        "profile.save": "Spara",
        "profile.identity.title": "Identitet",
        "profile.identity.saved": "Profilen uppdaterad",
        "profile.identity.no_change": "Inget att spara.",
        "profile.email.password_label": "Bekräfta nuvarande lösenord",
        "profile.email.password_placeholder": "Krävs för att byta e-post",
        "profile.email.hint": "Byte av e-post kräver ditt nuvarande lösenord.",
        "profile.email.need_password": "Ange ditt nuvarande lösenord för att byta e-post.",
        "profile.password.title": "Byt lösenord",
        "profile.password.current": "Nuvarande lösenord",
        "profile.password.new": "Nytt lösenord",
        "profile.password.confirm": "Bekräfta nytt lösenord",
        "profile.password.save": "Uppdatera lösenord",
        "profile.password.saved": "Lösenord uppdaterat",
        "profile.password.mismatch": "De nya lösenorden matchar inte.",
        "profile.password.too_short": "Nytt lösenord måste ha minst 6 tecken.",
        "profile.pools.title": "Tipsgäng",
        "profile.pools.empty": "Du är inte med i något tipsgäng ännu.",
        "profile.pools.member": "Medlem",
        "profile.pools.you_admin": "Du är admin",
        "profile.pools.leave": "Lämna",
        "profile.pools.admin_cannot_leave": "Admin kan inte lämna sitt eget tipsgäng. Lämna över admin eller radera tipsgänget från tipsgängets sida.",
        "profile.leave.title": "Lämna detta tipsgäng?",
        "profile.leave.body1": "Att lämna {name} tar bort ditt medlemskap och raderar dina tips och bonussvar i detta tipsgäng.",
        "profile.leave.body2": "Detta går inte att ångra.",
        "profile.leave.confirm": "Ja, lämna tipsgänget",
        "profile.leave.done": "Lämnade tipsgänget · {n} tips borttagna",
        "profile.danger.title": "Riskzon",
        "profile.danger.body": "Att radera ditt konto anonymiserar dina uppgifter men sparar dina tips så att andra spelares poängställning förblir korrekt.",
        "profile.delete.password_label": "Bekräfta nuvarande lösenord",
        "profile.delete.button": "Radera konto",
        "profile.delete.title": "Radera ditt konto?",
        "profile.delete.body1": "Ditt namn ersätts med 'Raderad användare' och din e-post anonymiseras. Dina tips sparas så att övriga spelares poäng inte påverkas.",
        "profile.delete.body2": "Du loggas ut och kan inte logga in igen med detta konto.",
        "profile.delete.confirm": "Ja, radera mitt konto",
        "profile.delete.done": "Kontot är raderat. Hejdå!",
    },
};

// ------------------------------------------------------------------
// Context + hook
// ------------------------------------------------------------------
const I18nContext = createContext(null);

export function I18nProvider({ children }) {
    const [lang, setLangState] = useState(() => {
        try { return localStorage.getItem("vmtips_lang") || "en"; }
        catch (e) {
            logger.warn("i18n: localStorage read failed", e);
            return "en";
        }
    });

    const setLang = useCallback((next) => {
        const v = next === "sv" ? "sv" : "en";
        setLangState(v);
        try { localStorage.setItem("vmtips_lang", v); }
        catch (e) { logger.warn("i18n: localStorage write failed", e); }
    }, []);

    useEffect(() => {
        document.documentElement.lang = lang;
    }, [lang]);

    const t = useCallback((key, params) => {
        const dict = STRINGS[lang] || STRINGS.en;
        let s = dict[key];
        if (s == null) s = STRINGS.en[key] || key;
        if (params) {
            Object.entries(params).forEach(([k, v]) => {
                s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
            });
        }
        return s;
    }, [lang]);

    const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
    return ctx;
}
